import React from 'react';

/**
 * SelectWithOther — a native <select> with a permanent "Other (specify)"
 * option that reveals a small text input below when chosen. Replaces the
 * earlier DatalistInput pattern which had three problems flagged by the
 * founder:
 *   1. Once a user picked a datalist value, the dropdown would not
 *      re-open on subsequent focus (native datalist behavior).
 *   2. Adding a chevron button to force re-open produced a visible
 *      duplicate-chevron next to the browser's own indicator.
 *   3. The native datalist popup renders OS-styled (dark on dark) and
 *      ignores our Adaptive-Mode (light/sand) palette.
 *
 * A real <select> avoids all three: re-openable at any time, single
 * chevron, palette-respecting (uses the same styling token as the
 * subgroup/group selects elsewhere in the dialog).
 *
 * When `suggestions` is empty, falls back to a plain text input so a
 * new user (whose session history is empty) isn't faced with a
 * degenerate select that only contains "Other".
 */
const SelectWithOther = ({
  value,
  onChange,
  suggestions,
  placeholder,
  testId,
}) => {
  const list = suggestions || [];
  // `isOther` is true when the user picked "Other (specify)" or when the
  // current value isn't in the suggestion list (typical for free-text
  // edits round-tripped from saved sessions). In either case we expose
  // the freeform text input.
  const valueInList = !!value && list.includes(value);
  const [isOther, setIsOther] = React.useState(!!value && !valueInList);

  // Empty-suggestions degraded mode — plain text input.
  if (list.length === 0) {
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
        data-testid={testId}
      />
    );
  }

  const onSelect = (e) => {
    const v = e.target.value;
    if (v === '__OTHER__') {
      setIsOther(true);
      onChange('');
    } else {
      setIsOther(false);
      onChange(v);
    }
  };

  return (
    <div className="space-y-2">
      <select
        value={isOther ? '__OTHER__' : (valueInList ? value : '')}
        onChange={onSelect}
        className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
        data-testid={testId}
      >
        <option value="">{placeholder || 'Select…'}</option>
        {list.map((s) => <option key={s} value={s}>{s}</option>)}
        <option value="__OTHER__">Other (specify)…</option>
      </select>
      {isOther && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          placeholder="Type a custom value"
          className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
          data-testid={testId ? `${testId}-other-input` : undefined}
        />
      )}
    </div>
  );
};

export default SelectWithOther;
